import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PaginationState } from '../../helpers';
import { useQueryRequest } from './QueryRequestProvider.tsx';
import {
  useQueryResponseLoading,
  useQueryResponsePagination,
} from './QueryResponseProvider.tsx';

const ListPagination = () => {
  const pagination = useQueryResponsePagination();
  const isLoading = useQueryResponseLoading();
  const { t } = useTranslation();
  const { updateState } = useQueryRequest();
  const updatePage = (page: number | undefined | null) => {
    if (!page || isLoading || pagination.page === page) {
      return;
    }

    updateState({ page, items_per_page: pagination.items_per_page || 10 });
  };

  const PAGINATION_PAGES_COUNT = 5;
  const sliceLinks = (pagination?: PaginationState) => {
    if (!pagination?.links?.length) {
      return [];
    }

    const scopedLinks = [...pagination.links];

    let pageLinks: Array<{
      label: string;
      active: boolean;
      url: string | null;
      page: number | null;
    }> = [];
    const previousLink: {
      label: string;
      active: boolean;
      url: string | null;
      page: number | null;
    } = scopedLinks.shift()!;
    const nextLink: {
      label: string;
      active: boolean;
      url: string | null;
      page: number | null;
    } = scopedLinks.pop()!;

    const halfOfPagesCount = Math.floor(PAGINATION_PAGES_COUNT / 2);

    pageLinks.push({...previousLink, label:'Previous'});

    if (
      pagination.page <= Math.round(PAGINATION_PAGES_COUNT / 2) ||
      scopedLinks.length <= PAGINATION_PAGES_COUNT
    ) {
      pageLinks = [
        ...pageLinks,
        ...scopedLinks.slice(0, PAGINATION_PAGES_COUNT),
      ];
    }

    if (
      pagination.page > scopedLinks.length - halfOfPagesCount &&
      scopedLinks.length > PAGINATION_PAGES_COUNT
    ) {
      pageLinks = [
        ...pageLinks,
        ...scopedLinks.slice(
          scopedLinks.length - PAGINATION_PAGES_COUNT,
          scopedLinks.length
        ),
      ];
    }

    if (
      !(
        pagination.page <= Math.round(PAGINATION_PAGES_COUNT / 2) ||
        scopedLinks.length <= PAGINATION_PAGES_COUNT
      ) &&
      !(pagination.page > scopedLinks.length - halfOfPagesCount)
    ) {
      pageLinks = [
        ...pageLinks,
        ...scopedLinks.slice(
          pagination.page - 1 - halfOfPagesCount,
          pagination.page + halfOfPagesCount
        ),
      ];
    }

    pageLinks.push({...nextLink, label:'Next'});

    return pageLinks;
  };

  const paginationLinks = useMemo(() => sliceLinks(pagination), [pagination]);
  const lastPage = Math.max(1, ...(pagination.links ?? []).map(link => link.page ?? 1));

  return (
    <div className='row'>
      <div className='col-sm-12 col-md-5 d-flex align-items-center justify-content-center justify-content-md-start'></div>
      <div className='col-sm-12 col-md-7 d-flex align-items-center justify-content-center justify-content-md-end'>
        <nav id='kt_table_users_paginate' aria-label={t('Pagination')}>
          <ul className='pagination'>
            <li
              className={clsx('page-item', {
                disabled: isLoading || pagination.page === 1,
              })}
            >
              <button type='button'
                disabled={isLoading || pagination.page === 1}
                onClick={() => updatePage(1)}
                style={{ cursor: 'pointer' }}
                className='page-link'
              >
                {t('First')}
              </button>
            </li>
            {paginationLinks.map((link) => (
                <li
                  key={link.label}
                  className={clsx('page-item', {
                    active: pagination.page === link.page,
                    disabled: isLoading || !link.page || pagination.page === link.page,
                    previous: link.label === 'Previous',
                    next: link.label === 'Next',
                  })}
                >
                  <button type='button'
                    disabled={isLoading || !link.page || pagination.page === link.page}
                    aria-current={pagination.page === link.page ? 'page' : undefined}
                    className={clsx('page-link', {
                      'page-text':
                        link.label === 'Previous' || link.label === 'Next',
                      'me-5': link.label === 'Previous',
                    })}
                    onClick={() => updatePage(link.page)}
                    style={{ cursor: 'pointer' }}
                  >
                    {['Previous', 'Next'].includes(link.label) ? t(link.label) : link.label}
                  </button>
                </li>
              ))}
            <li
              className={clsx('page-item', {
                disabled:
                  isLoading ||
                  pagination.page === lastPage,
              })}
            >
              <button type='button'
                disabled={isLoading || pagination.page === lastPage}
                onClick={() => updatePage(lastPage)}
                style={{ cursor: 'pointer' }}
                className='page-link'
              >
                {t('Last')}
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default ListPagination;
